import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import {
  Loader2, MessageSquarePlus, Paperclip, Send, FileText,
  ArrowLeft, Circle, X, LifeBuoy, RefreshCw,
} from "lucide-react";

const STATUS_LABELS = {
  open: { label: "Aberto", cls: "bg-slate-100 text-slate-700" },
  awaiting_admin: { label: "Aguardando suporte", cls: "bg-amber-100 text-amber-800" },
  awaiting_user: { label: "Aguardando você", cls: "bg-sky-100 text-sky-800" },
  resolved: { label: "Resolvido", cls: "bg-emerald-100 text-emerald-800" },
  closed: { label: "Fechado", cls: "bg-slate-200 text-slate-600" },
};

const formatDate = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString("pt-BR", {
      day: "2-digit", month: "2-digit", year: "2-digit",
      hour: "2-digit", minute: "2-digit",
    });
  } catch (_e) { return iso; }
};

const formatSize = (n) => {
  if (!n) return "";
  if (n < 1024) return `${n}B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)}KB`;
  return `${(n / 1024 / 1024).toFixed(2)}MB`;
};

const StatusPill = ({ status }) => {
  const s = STATUS_LABELS[status] || STATUS_LABELS.open;
  return (
    <span className={`inline-block px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${s.cls}`}>
      {s.label}
    </span>
  );
};

function AttachmentPill({ att, onDownload }) {
  return (
    <button
      type="button"
      onClick={() => onDownload(att)}
      data-testid={`support-att-${att.id}`}
      className="inline-flex items-center gap-1.5 px-2 py-1 text-[11px] bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-sm border border-slate-200 font-mono"
      title={att.filename}
    >
      <Paperclip className="w-3 h-3" />
      <span className="max-w-[180px] truncate">{att.filename}</span>
      <span className="text-slate-400">· {formatSize(att.size)}</span>
    </button>
  );
}

function NewTicketForm({ onCreated, onCancel }) {
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const addFiles = (fList) => {
    const arr = Array.from(fList || []);
    const combined = [...files, ...arr].slice(0, 3);
    setFiles(combined);
  };

  const removeFile = (idx) => setFiles(files.filter((_, i) => i !== idx));

  const submit = async (e) => {
    e.preventDefault();
    if (subject.trim().length < 3) return toast.error("Assunto muito curto");
    if (body.trim().length < 3) return toast.error("Descreva o problema");
    setBusy(true);
    try {
      const fd = new FormData();
      files.forEach((f) => fd.append("files", f));
      const qs = new URLSearchParams({ subject: subject.trim(), body: body.trim() }).toString();
      const resp = await api.post(`/support/tickets?${qs}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 120000,
      });
      toast.success("Chamado aberto — você receberá resposta por e-mail");
      onCreated(resp.data);
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="border border-slate-200 bg-white p-5 space-y-4" data-testid="support-new-form">
      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">Novo chamado</div>
        <button type="button" onClick={onCancel} className="text-xs text-slate-500 hover:text-slate-900" data-testid="support-new-cancel">
          Cancelar
        </button>
      </div>
      <div>
        <label className="block text-[10px] uppercase tracking-widest font-bold text-slate-700 mb-1">Assunto</label>
        <input
          data-testid="support-new-subject"
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          maxLength={140}
          placeholder="Resuma o problema"
          className="w-full border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:border-[#002EA6]"
        />
      </div>
      <div>
        <label className="block text-[10px] uppercase tracking-widest font-bold text-slate-700 mb-1">Descrição</label>
        <textarea
          data-testid="support-new-body"
          value={body}
          onChange={(e) => setBody(e.target.value)}
          maxLength={8000}
          rows={6}
          placeholder="Conte o que aconteceu, erros que apareceram, passos para reproduzir…"
          className="w-full border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:border-[#002EA6] resize-y"
        />
      </div>
      <div>
        <label className="block text-[10px] uppercase tracking-widest font-bold text-slate-700 mb-1">
          Anexos <span className="text-slate-400 font-normal normal-case tracking-normal">(imagens/PDF, até 3 arquivos · 5 MB cada)</span>
        </label>
        <input
          ref={fileRef}
          data-testid="support-new-files"
          type="file"
          multiple
          accept="image/*,application/pdf,text/plain"
          onChange={(e) => addFiles(e.target.files)}
          className="hidden"
        />
        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          className="px-3 py-1.5 text-xs font-bold uppercase tracking-wider border border-slate-300 hover:border-slate-900"
        >
          <Paperclip className="w-3.5 h-3.5 inline mr-1" /> Selecionar arquivos
        </button>
        {files.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-1.5">
            {files.map((f, i) => (
              <span key={i} className="inline-flex items-center gap-1.5 px-2 py-1 text-[11px] bg-slate-100 border border-slate-200 rounded-sm">
                <FileText className="w-3 h-3 text-slate-500" />
                {f.name} <span className="text-slate-400">{formatSize(f.size)}</span>
                <button type="button" onClick={() => removeFile(i)} className="text-slate-400 hover:text-red-600 ml-1">
                  <X className="w-3 h-3" />
                </button>
              </span>
            ))}
          </div>
        )}
      </div>
      <div className="flex justify-end">
        <button
          type="submit"
          data-testid="support-new-submit"
          disabled={busy}
          className="px-5 py-2 text-xs font-bold uppercase tracking-wider bg-[#002EA6] hover:bg-[#00207A] text-white flex items-center gap-2 disabled:opacity-60"
        >
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
          Abrir chamado
        </button>
      </div>
    </form>
  );
}

function TicketThread({ ticketId, isAdmin, onBack, onChanged }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [reply, setReply] = useState("");
  const [replyFiles, setReplyFiles] = useState([]);
  const [sending, setSending] = useState(false);
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const path = isAdmin
        ? `/admin/support/tickets/${ticketId}`
        : `/support/tickets/${ticketId}`;
      const resp = await api.get(path);
      setData(resp.data);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  }, [ticketId, isAdmin]);

  useEffect(() => { load(); }, [load]);

  const downloadAttachment = async (att) => {
    try {
      const resp = await api.get(`/support/attachments/${att.id}`, { responseType: "blob" });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = att.filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 30000);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    }
  };

  const sendReply = async () => {
    if (reply.trim().length < 1) return;
    setSending(true);
    try {
      const fd = new FormData();
      replyFiles.forEach((f) => fd.append("files", f));
      const qs = new URLSearchParams({ body: reply.trim() }).toString();
      const path = isAdmin
        ? `/admin/support/tickets/${ticketId}/messages?${qs}`
        : `/support/tickets/${ticketId}/messages?${qs}`;
      await api.post(path, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 120000,
      });
      setReply("");
      setReplyFiles([]);
      await load();
      onChanged?.();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  const changeStatus = async (newStatus) => {
    try {
      await api.patch(`/admin/support/tickets/${ticketId}`, { status: newStatus });
      await load();
      onChanged?.();
      toast.success("Status atualizado");
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    }
  };

  if (busy && !data) {
    return (
      <div className="border border-slate-200 bg-white p-12 flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
      </div>
    );
  }
  if (!data) return null;
  const { ticket, messages } = data;
  const canWrite = ticket.status !== "closed";

  return (
    <div className="space-y-3" data-testid="support-thread">
      <div className="flex items-center justify-between">
        <button
          type="button"
          onClick={onBack}
          className="text-xs text-slate-500 hover:text-slate-900 flex items-center gap-1"
          data-testid="support-thread-back"
        >
          <ArrowLeft className="w-3 h-3" /> Voltar
        </button>
        <button
          type="button"
          onClick={load}
          className="text-xs text-slate-500 hover:text-slate-900 flex items-center gap-1"
        >
          <RefreshCw className="w-3 h-3" /> Atualizar
        </button>
      </div>

      <div className="border border-slate-200 bg-white">
        <div className="px-5 py-4 border-b border-slate-100 flex flex-wrap items-center justify-between gap-3">
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">
              Chamado #{ticket.id.slice(0, 8)}
            </div>
            <div className="text-lg font-black tracking-tight text-slate-900">{ticket.subject}</div>
            {isAdmin && (
              <div className="text-xs text-slate-500 font-mono mt-0.5">
                {ticket.user_name} · {ticket.user_email}
              </div>
            )}
          </div>
          <div className="flex items-center gap-2">
            <StatusPill status={ticket.status} />
            {isAdmin && (
              <select
                data-testid="support-status-select"
                value={ticket.status}
                onChange={(e) => changeStatus(e.target.value)}
                className="text-xs border border-slate-300 px-2 py-1 focus:outline-none focus:border-[#002EA6]"
              >
                <option value="open">Aberto</option>
                <option value="awaiting_admin">Aguardando suporte</option>
                <option value="awaiting_user">Aguardando cliente</option>
                <option value="resolved">Resolvido</option>
                <option value="closed">Fechado</option>
              </select>
            )}
          </div>
        </div>

        <div className="divide-y divide-slate-100" data-testid="support-messages">
          {messages.map((m) => {
            const mine = isAdmin ? m.author_role === "admin" : m.author_role === "user";
            return (
              <div key={m.id} className={`px-5 py-4 ${mine ? "bg-slate-50/60" : ""}`} data-testid={`support-msg-${m.id}`}>
                <div className="flex items-center justify-between gap-2 mb-1.5">
                  <div className="flex items-center gap-2">
                    <Circle className={`w-2 h-2 fill-current ${m.author_role === "admin" ? "text-[#002EA6]" : "text-slate-400"}`} />
                    <div className="text-xs font-bold text-slate-900">
                      {m.author_role === "admin" ? "Suporte Vyntric" : (m.author_name || m.author_email)}
                    </div>
                    <div className="text-[10px] uppercase tracking-widest text-slate-400">
                      {m.author_role === "admin" ? "Suporte" : "Cliente"}
                    </div>
                  </div>
                  <div className="text-[11px] text-slate-500 font-mono">{formatDate(m.created_at)}</div>
                </div>
                <div className="text-sm text-slate-800 whitespace-pre-wrap leading-relaxed">{m.body}</div>
                {m.attachments?.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {m.attachments.map((att) => (
                      <AttachmentPill key={att.id} att={att} onDownload={downloadAttachment} />
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {canWrite ? (
          <div className="border-t border-slate-200 p-4 bg-slate-50" data-testid="support-reply-form">
            <textarea
              data-testid="support-reply-body"
              value={reply}
              onChange={(e) => setReply(e.target.value)}
              rows={3}
              placeholder={isAdmin ? "Escreva a resposta ao cliente…" : "Escreva sua resposta…"}
              className="w-full border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:border-[#002EA6] resize-y bg-white"
            />
            <input
              ref={fileRef}
              type="file"
              multiple
              accept="image/*,application/pdf,text/plain"
              onChange={(e) => setReplyFiles([...Array.from(e.target.files || [])].slice(0, 3))}
              className="hidden"
            />
            <div className="mt-2 flex flex-wrap items-center justify-between gap-2">
              <div className="flex items-center gap-2 flex-wrap">
                <button
                  type="button"
                  onClick={() => fileRef.current?.click()}
                  className="px-2.5 py-1.5 text-[11px] font-bold uppercase tracking-wider border border-slate-300 hover:border-slate-900"
                >
                  <Paperclip className="w-3 h-3 inline mr-1" /> Anexar
                </button>
                {replyFiles.map((f, i) => (
                  <span key={i} className="inline-flex items-center gap-1 px-2 py-1 text-[11px] bg-white border border-slate-200 rounded-sm">
                    {f.name} <span className="text-slate-400">{formatSize(f.size)}</span>
                    <button type="button" onClick={() => setReplyFiles(replyFiles.filter((_, j) => j !== i))} className="ml-1 text-slate-400 hover:text-red-600">
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
              </div>
              <button
                type="button"
                data-testid="support-reply-send"
                onClick={sendReply}
                disabled={sending || reply.trim().length < 1}
                className="px-4 py-2 text-xs font-bold uppercase tracking-wider bg-[#002EA6] hover:bg-[#00207A] text-white flex items-center gap-2 disabled:opacity-60"
              >
                {sending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                Enviar resposta
              </button>
            </div>
          </div>
        ) : (
          <div className="border-t border-slate-200 p-4 bg-slate-100 text-xs text-slate-500 text-center">
            Este chamado está fechado. Abra um novo para novas dúvidas.
          </div>
        )}
      </div>
    </div>
  );
}

export function SupportPage({ isAdmin = false }) {
  const [tickets, setTickets] = useState([]);
  const [busy, setBusy] = useState(false);
  const [newForm, setNewForm] = useState(false);
  const [selected, setSelected] = useState(null);
  const [statusFilter, setStatusFilter] = useState("");
  const [params, setParams] = useSearchParams();

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const path = isAdmin
        ? `/admin/support/tickets${statusFilter ? `?status=${statusFilter}` : ""}`
        : "/support/tickets";
      const resp = await api.get(path);
      setTickets(Array.isArray(resp.data) ? resp.data : []);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  }, [isAdmin, statusFilter]);

  useEffect(() => { load(); }, [load]);

  // Deep-link ?ticket=<id> from e-mail buttons
  useEffect(() => {
    const t = params.get("ticket");
    if (t) setSelected(t);
  }, [params]);

  const openTicket = (id) => {
    setSelected(id);
    setParams({ ticket: id }, { replace: true });
  };
  const closeTicket = () => {
    setSelected(null);
    setParams({}, { replace: true });
    load();
  };

  const counts = useMemo(() => {
    const c = { total: tickets.length, awaiting_admin: 0, awaiting_user: 0, resolved: 0 };
    tickets.forEach((t) => { if (c[t.status] != null) c[t.status] += 1; });
    return c;
  }, [tickets]);

  if (selected) {
    return (
      <div className="space-y-4">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-slate-500">
            {isAdmin ? "Admin · Suporte" : "Suporte"}
          </div>
          <h1 className="text-3xl font-black tracking-tighter">
            {isAdmin ? "Painel de Suporte" : "Central de Suporte"}
          </h1>
        </div>
        <TicketThread
          ticketId={selected}
          isAdmin={isAdmin}
          onBack={closeTicket}
          onChanged={load}
        />
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="support-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-slate-500">
            {isAdmin ? "Admin · Suporte" : "Suporte"}
          </div>
          <h1 className="text-3xl font-black tracking-tighter">
            {isAdmin ? "Painel de Suporte" : "Central de Suporte"}
          </h1>
          <p className="text-sm text-slate-600 mt-1 max-w-2xl">
            {isAdmin
              ? "Chamados abertos por todos os clientes. Responda direto por aqui — o usuário recebe notificação por e-mail."
              : "Encontrou algum erro ou tem alguma dúvida? Abra um chamado e responderemos por aqui e por e-mail. Não é em tempo real, mas respondemos assim que possível."}
          </p>
        </div>
        {!isAdmin && !newForm && (
          <button
            type="button"
            onClick={() => setNewForm(true)}
            data-testid="support-open-new"
            className="px-4 py-2 text-xs font-bold uppercase tracking-wider bg-[#002EA6] hover:bg-[#00207A] text-white flex items-center gap-2"
          >
            <MessageSquarePlus className="w-3.5 h-3.5" /> Abrir chamado
          </button>
        )}
      </div>

      {isAdmin && (
        <div className="border border-slate-200 bg-white px-4 py-3 flex flex-wrap items-center gap-3">
          <div className="text-[10px] uppercase tracking-widest text-slate-600 font-bold">Filtro</div>
          <select
            data-testid="support-filter-status"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="text-xs border border-slate-300 px-2 py-1 focus:outline-none focus:border-[#002EA6]"
          >
            <option value="">Todos ({counts.total})</option>
            <option value="awaiting_admin">Aguardando suporte ({counts.awaiting_admin})</option>
            <option value="awaiting_user">Aguardando cliente ({counts.awaiting_user})</option>
            <option value="resolved">Resolvidos</option>
            <option value="closed">Fechados</option>
          </select>
          <button
            type="button"
            onClick={load}
            className="ml-auto text-xs text-slate-500 hover:text-slate-900 flex items-center gap-1"
          >
            <RefreshCw className="w-3 h-3" /> Atualizar
          </button>
        </div>
      )}

      {newForm && !isAdmin && (
        <NewTicketForm
          onCancel={() => setNewForm(false)}
          onCreated={(t) => { setNewForm(false); load(); openTicket(t.id); }}
        />
      )}

      <div className="border border-slate-200 bg-white" data-testid="support-ticket-list">
        {busy && !tickets.length ? (
          <div className="p-12 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>
        ) : tickets.length === 0 ? (
          <div className="p-12 text-center text-sm text-slate-500" data-testid="support-empty">
            <LifeBuoy className="w-8 h-8 mx-auto text-slate-300 mb-3" />
            {isAdmin ? "Nenhum chamado por aqui." : "Você ainda não abriu nenhum chamado."}
          </div>
        ) : (
          <div className="divide-y divide-slate-100">
            {tickets.map((t) => {
              const unread = isAdmin ? t.unread_for_admin : t.unread_for_user;
              return (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => openTicket(t.id)}
                  data-testid={`support-ticket-${t.id}`}
                  className="w-full text-left px-5 py-4 hover:bg-slate-50/70 flex items-center justify-between gap-4"
                >
                  <div className="min-w-0 flex items-center gap-3">
                    {unread && <span className="w-2 h-2 rounded-full bg-[#002EA6] shrink-0" />}
                    <div className="min-w-0">
                      <div className="text-sm font-bold text-slate-900 truncate">{t.subject}</div>
                      <div className="text-[11px] text-slate-500 font-mono flex flex-wrap gap-x-2 gap-y-0.5 mt-0.5">
                        <span>#{t.id.slice(0, 8)}</span>
                        {isAdmin && <span>· {t.user_email}</span>}
                        <span>· {t.message_count} msg{t.message_count > 1 ? "s" : ""}</span>
                        <span>· última atividade {formatDate(t.last_message_at)}</span>
                      </div>
                    </div>
                  </div>
                  <StatusPill status={t.status} />
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

export default function Support() {
  return <SupportPage isAdmin={false} />;
}
